import { useState } from "react";
import { Save, Bell, ShieldCheck, Mail, MessageSquare, Smartphone } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { memberProfile } from "@/lib/dummyData";
import { useAuth } from "@/lib/auth";
import { useEffect } from "react";

export default function MemberSettings() {
  const { user, updateProfile, apiError } = useAuth();
  const [notif, setNotif] = useState({ email: true, wa: true, push: false, news: true });

  // Form profil terkontrol, diisi dari data user yang login.
  const [form, setForm] = useState({ name: "", email: "", phone_number: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (user) setForm({ name: user.name || "", email: user.email || "", phone_number: user.phone_number || "" });
  }, [user]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Profil diperbarui");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };
  const initials = (user?.name || "AR").split(" ").map((p) => p[0]).slice(0, 2).join("");
  return (
    <div className="space-y-7 max-w-5xl">
      <div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Pengaturan</h1>
        <p className="text-ink/75 mt-2">Perbarui data pribadi, preferensi notifikasi, dan keamanan akun.</p>
      </div>

      <Card className="rounded-3xl border-stone-soft p-7">
        <h3 className="font-display font-semibold text-ink">Profil</h3>
        <Separator className="my-5 bg-stone-soft/60" />
        <div className="flex items-center gap-5 mb-7">
          <Avatar className="w-20 h-20 ring-4 ring-amber-warm/30">
            <AvatarFallback className="bg-forest/10 text-forest font-bold text-lg">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <Button variant="outline" className="rounded-full border-stone-soft" data-testid="profile-photo-change">Ubah Foto</Button>
            <p className="text-xs text-ink/55 mt-2">JPG/PNG, maks 2 MB</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Nama Lengkap</Label>
            <Input value={form.name} onChange={set("name")} className="bg-cream border-stone-soft rounded-lg" data-testid="set-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Email</Label>
            <Input value={form.email} onChange={set("email")} className="bg-cream border-stone-soft rounded-lg" data-testid="set-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">No. Handphone</Label>
            <Input value={form.phone_number} onChange={set("phone_number")} className="bg-cream border-stone-soft rounded-lg" data-testid="set-phone" />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={saveProfile} disabled={saving} className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="settings-save-profile">
            <Save size={15} /> {saving ? "Menyimpan…" : "Simpan Perubahan"}
          </Button>
        </div>
      </Card>

      <Card className="rounded-3xl border-stone-soft p-7">
        <div className="flex items-center gap-2">
          <Bell className="text-forest" size={18} />
          <h3 className="font-display font-semibold text-ink">Notifikasi</h3>
        </div>
        <Separator className="my-5 bg-stone-soft/60" />
        <div className="space-y-4">
          {[
            { k: "email", l: "Notifikasi Email", d: "Mutasi simpanan, status pesanan, dan SHU", icon: Mail },
            { k: "wa", l: "WhatsApp", d: "Update penting via WhatsApp resmi koperasi", icon: MessageSquare },
            { k: "push", l: "Notifikasi Push", d: "Push notifikasi di browser dan aplikasi", icon: Smartphone },
            { k: "news", l: "Berita & Pengumuman", d: "Kabar terbaru dari koperasi", icon: Bell },
          ].map((n) => (
            <div key={n.k} className="flex items-center justify-between py-2">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-cream grid place-items-center"><n.icon size={16} className="text-forest" /></div>
                <div>
                  <div className="font-medium text-sm text-ink">{n.l}</div>
                  <div className="text-xs text-ink/75">{n.d}</div>
                </div>
              </div>
              <Switch checked={notif[n.k]} onCheckedChange={(v) => setNotif({ ...notif, [n.k]: v })} data-testid={`notif-${n.k}`} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="rounded-3xl border-stone-soft p-7">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-forest" size={18} />
          <h3 className="font-display font-semibold text-ink">Keamanan</h3>
        </div>
        <Separator className="my-5 bg-stone-soft/60" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Sandi Saat Ini" type="password" testid="sec-current" />
          <Field label="Sandi Baru" type="password" testid="sec-new" />
          <Field label="Konfirmasi Sandi Baru" type="password" testid="sec-confirm" />
        </div>
        <div className="flex items-center justify-between mt-6 py-2">
          <div>
            <div className="font-medium text-sm text-ink">Autentikasi Dua Faktor (2FA)</div>
            <div className="text-xs text-ink/75">Kode OTP via WhatsApp setiap login dari perangkat baru</div>
          </div>
          <Switch defaultChecked data-testid="2fa-switch" />
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={() => toast.success("Pengaturan keamanan disimpan")} className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="settings-save-security">
            <Save size={15} /> Perbarui Keamanan
          </Button>
        </div>
      </Card>
    </div>
  );
}

const Field = ({ label, type = "text", defaultValue, testid }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-ink/55 uppercase tracking-wider">{label}</Label>
    <Input type={type} defaultValue={defaultValue} className="bg-cream border-stone-soft rounded-lg" data-testid={testid} />
  </div>
);
